import type { APIResponseOneBox3 } from '~/api/baseFetch/common.type';
import type {
  TOneBoxV3DropDownList,
  TOneBoxV3DropDownListPayload,
  TOneBoxV3DropDownListSearch,
} from '~/api/oneBoxV3/oneBoxV3.type';
import { SearchCategory } from '~/types/enum';

/** 後台多選全科目時，避免一次打出過多 dropDownList 請求（UAT 易逾時／失敗） */
export const LATEST_NEWS_DROPDOWN_BATCH_SIZE = 6;

type TUniqPropKey = 'years' | 'grades' | 'subjects';

type TDropDownTrigger = (arg: TOneBoxV3DropDownListPayload) => {
  unwrap: () => Promise<APIResponseOneBox3<TOneBoxV3DropDownList>>;
};

const mergePropValues = (
  results: APIResponseOneBox3<TOneBoxV3DropDownList>[],
  prop: TUniqPropKey,
): string[] => {
  const merged = new Set<string>();
  for (const res of results) {
    if (res.err !== 0) continue;
    const list = res.data?.uniqResourceProps?.[prop] ?? [];
    list.forEach(v => merged.add(v));
  }
  return [...merged];
};

/**
 * 依「學程 × 科目」分批請求 dropDownList 並合併 uniqResourceProps。
 * UAT 不帶 subjects 時學年度常為空；一次 Promise.all 全打又易失敗，故採分批 + allSettled。
 */
export async function fetchMergedDropdownByEduAndSubjects(
  eduCodes: string[],
  subjects: string[],
  prop: TUniqPropKey,
  trigger: TDropDownTrigger,
  buildSearch: (edu: string, subject: string) => TOneBoxV3DropDownListSearch,
  categories: SearchCategory[] = [SearchCategory.SCHOOL],
): Promise<string[]> {
  if (!eduCodes.length || !subjects.length) return [];

  const pairs = eduCodes.flatMap(edu => subjects.map(subj => ({ edu, subj })));

  const collected: APIResponseOneBox3<TOneBoxV3DropDownList>[] = [];

  for (let i = 0; i < pairs.length; i += LATEST_NEWS_DROPDOWN_BATCH_SIZE) {
    const batch = pairs.slice(i, i + LATEST_NEWS_DROPDOWN_BATCH_SIZE);
    const settled = await Promise.allSettled(
      batch.map(({ edu, subj }) =>
        trigger({
          isLaunch: true,
          categories,
          resourceProps: [prop],
          search: buildSearch(edu, subj),
        }).unwrap(),
      ),
    );
    for (const s of settled) {
      if (s.status === 'fulfilled') collected.push(s.value);
    }
  }

  return mergePropValues(collected, prop);
}
